/**
 * InitiativeMergePanel - Entity-specific merge panel for Initiatives
 * 
 * Provides a familiar tab-based UI (Overview | Goals | KPIs | Epics) for both
 * source items (view mode) and merged result (edit mode).
 * 
 * This replaces the generic dynamic merge UI when entityType === 'initiative',
 * giving users a clear understanding of what data is being kept vs lost.
 * 
 * Props:
 * - sourceItems: array of initiative objects fetched from DB
 * - selectedItems: { [idx]: boolean } - which source items are included
 * - expandedItems: { [idx]: boolean } - which source items are expanded
 * - onToggleSelect: (idx) => void
 * - onToggleExpand: (idx) => void
 * - formData: merged initiative form data
 * - onFormChange: (field, value) => void
 * - productId: string
 * - errors: { [field]: string } - validation errors
 */

import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { teamApi, goalsApi } from '../../services/api';
import {
  ChevronDown,
  ChevronRight,
  Rocket,
  Target,
  BarChart3,
  Layers,
  Plus,
  Minus,
  AlertTriangle,
} from 'lucide-react';

// Import reusable form components
import InitiativeOverviewForm, { INITIATIVE_STATUS_OPTIONS } from '../initiative/InitiativeOverviewForm';
import InitiativeGoalsPanel from '../initiative/InitiativeGoalsPanel';
import InitiativeEpicsPanel from '../initiative/InitiativeEpicsPanel';

// =============================================================================
// SOURCE INITIATIVE CARD (Left Panel)
// =============================================================================

const SourceInitiativeCard = ({
  initiative,
  index,
  isSelected,
  isExpanded,
  onToggleSelect,
  onToggleExpand,
  teamMembers,
  allGoals,
  colors,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const statusConfig = INITIATIVE_STATUS_OPTIONS.find(s => s.value === initiative.status) || INITIATIVE_STATUS_OPTIONS[0];
  const ownerName = teamMembers.find(m => m.user_id === initiative.owner_id)?.name || 'Unassigned';
  const goalCount = initiative.goal_ids?.length || 0;
  const epicCount = initiative.linked_epics?.length || 0;
  const kpiCount = initiative.subscribed_kpi_ids?.length || 0;

  return (
    <div 
      className={cn(
        "rounded-lg border overflow-hidden transition-all",
        isSelected 
          ? "border-purple-300 dark:border-purple-600 bg-white dark:bg-gray-800" 
          : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60"
      )}
      data-testid={`source-initiative-${index}`}
    >
      {/* Header - Always Visible */}
      <div 
        className={cn(
          "flex items-center gap-3 p-3 cursor-pointer",
          "hover:bg-gray-50 dark:hover:bg-gray-700/50"
        )}
        onClick={onToggleExpand}
      >
        {/* Selection Checkbox */}
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            className="border-gray-400"
          />
        </div>
        
        {/* Expand/Collapse Icon */}
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
        )}
        
        {/* Initiative Icon */}
        <div className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          isSelected ? "bg-purple-100 dark:bg-purple-900/30" : "bg-gray-200 dark:bg-gray-700"
        )}>
          <Rocket className={cn("w-4 h-4", isSelected ? "text-purple-600" : "text-gray-500")} />
        </div>
        
        {/* Initiative Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn(
              "font-medium truncate",
              isSelected ? "text-gray-900 dark:text-gray-100" : "text-gray-500 line-through"
            )}>
              {initiative.title || 'Untitled Initiative'}
            </span>
            {!isSelected && (
              <Badge variant="outline" className="text-xs text-red-500 border-red-300">
                Excluded
              </Badge>
            )}
          </div>
          {!isExpanded && (
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
              <span className={cn("px-1.5 py-0.5 rounded", statusConfig.color, "text-white text-[10px]")}>
                {statusConfig.label}
              </span>
              <span>•</span>
              <span>{ownerName}</span>
              {goalCount > 0 && (
                <>
                  <span>•</span>
                  <span>{goalCount} goal{goalCount !== 1 ? 's' : ''}</span>
                </>
              )}
              {epicCount > 0 && (
                <>
                  <span>•</span>
                  <span>{epicCount} epic{epicCount !== 1 ? 's' : ''}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Expanded Content - Tab-based View */}
      {isExpanded && (
        <div className={cn("border-t", colors.borderPrimary)}>
          {/* Tabs */}
          <div className="px-3 pt-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-gray-100 dark:bg-gray-800">
                <TabsTrigger value="overview" className="text-xs">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="goals" className="text-xs">
                  Goals ({goalCount})
                </TabsTrigger>
                <TabsTrigger value="kpis" className="text-xs">
                  KPIs ({kpiCount})
                </TabsTrigger>
                <TabsTrigger value="epics" className="text-xs">
                  Epics ({epicCount})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          {/* Tab Content */}
          <div className="p-3">
            {activeTab === 'overview' && (
              <InitiativeOverviewForm
                formData={initiative}
                isEditing={false}
                isDisabled={true}
                colors={colors}
                teamMembers={teamMembers}
                showProgressFollows={true}
                mode="view"
              />
            )}
            
            {activeTab === 'goals' && (
              <InitiativeGoalsPanel
                linkedGoalIds={initiative.goal_ids || []}
                allGoals={allGoals}
                isEditing={false}
                colors={colors}
                mode="view"
              />
            )}
            
            {activeTab === 'kpis' && (
              <div className={cn("p-4 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
                <BarChart3 className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
                <p className={cn("text-sm", colors.textMuted)}>
                  {kpiCount > 0 
                    ? `${kpiCount} KPI subscription${kpiCount !== 1 ? 's' : ''} configured`
                    : 'No KPI subscriptions'}
                </p>
                <p className={cn("text-xs mt-1", colors.textMuted)}>
                  KPI subscriptions will be combined in the merged result
                </p>
              </div>
            )}
            
            {activeTab === 'epics' && (
              <InitiativeEpicsPanel
                linkedEpics={initiative.linked_epics || []}
                isEditing={false}
                colors={colors}
                mode="view"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MERGE DIFF SUMMARY
// =============================================================================

const MergeDiffSummary = ({ sourceItems, selectedItems, mergedGoalIds, mergedKpiIds, colors }) => {
  // Calculate what's being kept vs lost
  const selectedInitiatives = sourceItems.filter((_, idx) => selectedItems[idx]);
  const excludedInitiatives = sourceItems.filter((_, idx) => !selectedItems[idx]);
  
  // Count goals
  const totalSourceGoals = new Set(selectedInitiatives.flatMap(i => i.goal_ids || [])).size;
  const excludedGoals = new Set(excludedInitiatives.flatMap(i => i.goal_ids || [])).size;
  
  // Count KPIs
  const totalSourceKpis = new Set(selectedInitiatives.flatMap(i => i.subscribed_kpi_ids || [])).size;
  const excludedKpis = new Set(excludedInitiatives.flatMap(i => i.subscribed_kpi_ids || [])).size;
  
  // Count epics
  const totalSourceEpics = selectedInitiatives.reduce((sum, i) => sum + (i.linked_epics?.length || 0), 0);
  const excludedEpics = excludedInitiatives.reduce((sum, i) => sum + (i.linked_epics?.length || 0), 0);

  if (excludedInitiatives.length === 0) {
    return null; // No diff to show
  }

  return (
    <div className={cn("p-3 rounded-lg border mb-4", "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700")}>
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="font-medium text-amber-800 dark:text-amber-200 text-sm">
          Merge Impact Summary
        </span>
      </div>
      <div className="space-y-1 text-xs">
        {excludedInitiatives.length > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              {excludedInitiatives.length} initiative(s) excluded: {excludedInitiatives.map(i => `"${i.title}"`).join(', ')}
            </span>
          </div>
        )}
        {excludedGoals > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              Goal links from excluded initiatives may be lost
            </span>
          </div>
        )}
        {excludedEpics > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              {excludedEpics} epic(s) from excluded initiatives will be unlinked
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 pt-1 border-t border-amber-300 dark:border-amber-600 mt-2">
          <Plus className="w-3 h-3 text-green-500" />
          <span className="text-green-700 dark:text-green-300">
            Merged initiative will have goals and KPIs from {selectedInitiatives.length} selected initiative(s)
          </span>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MERGED INITIATIVE FORM (Right Panel)
// =============================================================================

const MergedInitiativeForm = ({
  formData,
  onFormChange,
  teamMembers,
  allGoals,
  colors,
  errors = {},
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const handleChange = (field, value) => {
    onFormChange(field, value);
  };
  
  // Goal handlers
  const handleLinkGoal = (goalId) => {
    if (!formData.goal_ids?.includes(goalId)) {
      onFormChange('goal_ids', [...(formData.goal_ids || []), goalId]);
    }
  };
  
  const handleUnlinkGoal = (goalId) => {
    onFormChange('goal_ids', (formData.goal_ids || []).filter(id => id !== goalId));
  };

  const goalCount = formData.goal_ids?.length || 0;
  const kpiCount = formData.subscribed_kpi_ids?.length || 0;
  const epicCount = formData.linked_epics?.length || 0;

  // Check if there are errors in overview fields
  const hasOverviewErrors = Object.keys(errors).some(k => ['title', 'description'].includes(k));

  return (
    <div className="h-full flex flex-col">
      {/* Tabs */}
      <div className="flex-shrink-0 pb-2">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={cn("bg-gray-100 dark:bg-gray-800")}>
            <TabsTrigger 
              value="overview" 
              className={cn("text-sm", hasOverviewErrors && "text-red-500")}
            >
              Overview {hasOverviewErrors && '⚠️'}
            </TabsTrigger>
            <TabsTrigger value="goals" className="text-sm">
              Goals ({goalCount})
            </TabsTrigger>
            <TabsTrigger value="kpis" className="text-sm">
              KPIs ({kpiCount})
            </TabsTrigger>
            <TabsTrigger value="epics" className="text-sm">
              Epics ({epicCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && (
          <InitiativeOverviewForm
            formData={formData}
            onChange={handleChange}
            isEditing={true}
            isDisabled={false}
            colors={colors}
            teamMembers={teamMembers}
            showProgressFollows={true}
            errors={errors}
            mode="create"
          />
        )}
        
        {activeTab === 'goals' && (
          <InitiativeGoalsPanel
            linkedGoalIds={formData.goal_ids || []}
            allGoals={allGoals}
            onLink={handleLinkGoal}
            onUnlink={handleUnlinkGoal}
            isEditing={true}
            colors={colors}
            mode="edit"
          />
        )}
        
        {activeTab === 'kpis' && (
          <div className={cn("p-4 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
            <BarChart3 className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
            <p className={cn("text-sm font-medium", colors.textPrimary)}>
              {kpiCount} KPI subscription{kpiCount !== 1 ? 's' : ''} will be merged
            </p>
            <p className={cn("text-xs mt-2", colors.textMuted)}>
              KPI subscriptions from all selected initiatives will be combined.
              You can adjust them after the merge is complete.
            </p>
          </div>
        )}
        
        {activeTab === 'epics' && (
          <div className={cn("p-4 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
            <Layers className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
            <p className={cn("text-sm font-medium", colors.textPrimary)}>
              {epicCount} epic{epicCount !== 1 ? 's' : ''} will be re-linked
            </p>
            <p className={cn("text-xs mt-2", colors.textMuted)}>
              All epics from selected initiatives will be moved to the merged initiative.
              You can manage them after the merge is complete.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const InitiativeMergePanel = ({
  sourceItems,
  selectedItems,
  expandedItems,
  onToggleSelect,
  onToggleExpand,
  formData,
  onFormChange,
  productId,
  errors = {},
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Team members for owner dropdown
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  
  // All product goals for linking
  const [allGoals, setAllGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(true);
  
  // Load team members
  useEffect(() => {
    const loadTeam = async () => {
      try {
        const res = await teamApi.list();
        const activeMembers = (res.data?.members || []).filter(m => m.status === 'active');
        setTeamMembers(activeMembers);
      } catch (err) {
        console.error('Failed to load team:', err);
      } finally {
        setLoadingTeam(false);
      }
    };
    loadTeam();
  }, []);
  
  // Load all product goals
  useEffect(() => {
    const loadGoals = async () => {
      if (!productId) return;
      try {
        const res = await goalsApi.list(productId);
        setAllGoals(res.data || []);
      } catch (err) {
        console.error('Failed to load goals:', err);
      } finally {
        setLoadingGoals(false);
      }
    };
    loadGoals();
  }, [productId]);
  
  // Selected count
  const selectedCount = Object.values(selectedItems).filter(Boolean).length;

  return (
    <div className="grid grid-cols-2 h-full divide-x divide-gray-200 dark:divide-gray-700">
      {/* Left Panel: Source Initiatives */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              Source Initiatives
            </h3>
            <Badge variant="outline">
              {selectedCount} of {sourceItems.length} selected
            </Badge>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Select initiatives to include in the merge. Expand to review data.
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {sourceItems.map((initiative, idx) => (
            <SourceInitiativeCard
              key={initiative.initiative_id || idx}
              initiative={initiative}
              index={idx}
              isSelected={selectedItems[idx]}
              isExpanded={expandedItems[idx]}
              onToggleSelect={() => onToggleSelect(idx)}
              onToggleExpand={() => onToggleExpand(idx)}
              teamMembers={teamMembers}
              allGoals={allGoals}
              colors={colors}
            />
          ))}
        </div>
      </div>
      
      {/* Right Panel: Merged Initiative Form */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">
            Merged Result
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Edit the combined initiative before applying
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {/* Diff Summary */}
          <MergeDiffSummary
            sourceItems={sourceItems}
            selectedItems={selectedItems}
            mergedGoalIds={formData.goal_ids}
            mergedKpiIds={formData.subscribed_kpi_ids}
            colors={colors}
          />
          
          {/* Merged Form */}
          <MergedInitiativeForm
            formData={formData}
            onFormChange={onFormChange}
            teamMembers={teamMembers}
            allGoals={allGoals}
            colors={colors}
            errors={errors}
          />
        </div>
      </div>
    </div>
  );
};

export default InitiativeMergePanel;
